/**
 * Calendário de publicações: Instagram, Facebook, TikTok, YouTube e LinkedIn.
 *
 * O mês inteiro de relance — o que vai sair, o que saiu e o que falhou — com
 * os rascunhos sem data ao lado. Clicar num dia abre um post novo naquele dia;
 * clicar num post abre a edição (ou o andamento, se já começou a sair).
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { PermissionGuard } from "@/components/PermissionGuard";
import DashboardLayout from "@/components/DashboardLayout";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  FlaskConical,
  Image as ImageIcon,
  Link2,
  Plus,
  Smartphone,
  UserPlus,
} from "lucide-react";
import { Link } from "wouter";
import { usePermissions } from "@/hooks/usePermissions";
import { IconeRede } from "@/features/social/redes";
import { DIAS_CURTOS, diasDoMes, ehHoje, hhmm, mesmoDia } from "@/components/Agenda/agenda-utils";
import { POST_STATUS_LABELS, type PostStatus } from "@shared/social-publishing";
import PostComposer, { type PostDaTela } from "@/features/social/PostComposer";

const COR_STATUS: Record<string, string> = {
  draft: "border-dashed border-muted-foreground/40 bg-background",
  scheduled: "border-sky-300 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/40",
  publishing: "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40",
  published: "border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40",
  partial: "border-orange-300 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/40",
  failed: "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/40",
  canceled: "border-muted bg-muted/40 opacity-60",
};

function IconeFormato({ formato }: { formato: string }) {
  if (formato === "reel") return <Clapperboard className="h-3 w-3 shrink-0" />;
  if (formato === "story") return <Smartphone className="h-3 w-3 shrink-0" />;
  return <ImageIcon className="h-3 w-3 shrink-0" />;
}

function ChipPost({ p, onClick }: { p: PostDaTela; onClick: () => void }) {
  const thumb = p.media.find((m) => m.previewUrl)?.previewUrl;
  const redes = new Set(p.targets.map((t) => t.network));
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`flex w-full items-center gap-1 rounded border px-1 py-0.5 text-left text-[11px] leading-tight ${COR_STATUS[p.status] ?? ""}`}
      title={`${POST_STATUS_LABELS[p.status as PostStatus] ?? p.status} · ${p.caption.slice(0, 80)}`}
    >
      {thumb ? <img src={thumb} alt="" className="h-4 w-4 shrink-0 rounded-sm object-cover" /> : <IconeFormato formato={p.format} />}
      {p.scheduledAt && <span className="shrink-0 font-medium">{hhmm(p.scheduledAt)}</span>}
      <span className="truncate">{p.caption || (p.format === "story" ? "Story" : "Sem legenda")}</span>
      <span className="ml-auto flex shrink-0 gap-0.5">
        {p.trialGraduation && <FlaskConical className="h-3 w-3" />}
        {[...redes].map((r) => (
          <IconeRede key={r} rede={r} className="h-3 w-3" colorido={false} />
        ))}
      </span>
    </button>
  );
}

function SocialCalendarContent() {
  const [mes, setMes] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [aberto, setAberto] = useState(false);
  // Só o id: o post mostrado no diálogo vem sempre da lista mais recente —
  // senão "Tentar de novo" e o andamento de "Publicando" ficavam congelados.
  const [selecionadoId, setSelecionadoId] = useState<number | null>(null);
  const [diaNovo, setDiaNovo] = useState<Date | null>(null);

  const dias = useMemo(() => diasDoMes(mes), [mes]);
  const intervalo = useMemo(
    () => ({ from: dias[0], to: new Date(dias[dias.length - 1].getTime() + 864e5) }),
    [dias],
  );

  const status = trpc.social.status.useQuery(undefined, { staleTime: 60_000 });
  const { can: pode } = usePermissions();
  const posts = trpc.social.posts.list.useQuery(
    { ...intervalo, incluirRascunhos: true },
    // Acompanha sozinha só enquanto há algo saindo ou para sair nos próximos
    // 10 minutos — post agendado para daqui a semanas não justifica consultar
    // a cada 20 s o dia inteiro.
    {
      refetchInterval: (q) =>
        (q.state.data ?? []).some(
          (p) =>
            p.status === "publishing" ||
            (p.status === "scheduled" && p.scheduledAt && new Date(p.scheduledAt).getTime() - Date.now() < 10 * 60_000),
        )
          ? 20_000
          : false,
    },
  );
  // `/social?post=ID`: rascunho mandado de fora (o gerador de carrosséis e cortes)
  // abre direto para conferir e agendar, no mês da data sugerida.
  const search = useSearch();
  const [, navegar] = useLocation();
  const postDoLink = Number(new URLSearchParams(search).get("post")) || null;
  const vindoDoLink = trpc.social.posts.get.useQuery({ id: postDoLink ?? 0 }, { enabled: postDoLink != null, retry: false });
  const [doLink, setDoLink] = useState<PostDaTela | null>(null);
  useEffect(() => {
    const p = vindoDoLink.data;
    if (!p) return;
    setDoLink(p);
    if (p.scheduledAt) {
      const d = new Date(p.scheduledAt);
      setMes(new Date(d.getFullYear(), d.getMonth(), 1));
    }
    setSelecionadoId(p.id);
    setDiaNovo(null);
    setAberto(true);
    navegar("/social", { replace: true });
  }, [vindoDoLink.data, navegar]);
  // Até a lista do mês chegar, mostra o que veio do link — senão o diálogo
  // piscaria como "novo post".
  const selecionado =
    (posts.data ?? []).find((p) => p.id === selecionadoId) ?? (doLink?.id === selecionadoId ? doLink : null);

  const rascunhosSemData = (posts.data ?? []).filter((p) => !p.scheduledAt && p.status !== "canceled");
  const porDia = (d: Date) => (posts.data ?? []).filter((p) => p.scheduledAt && mesmoDia(new Date(p.scheduledAt), d));

  const abrirNovo = (dia: Date | null) => {
    setSelecionadoId(null);
    setDiaNovo(dia);
    setAberto(true);
  };
  const abrirPost = (p: PostDaTela) => {
    setSelecionadoId(p.id);
    setDiaNovo(null);
    setAberto(true);
  };

  // "outubro de 2026" → "Outubro de 2026" (o `capitalize` do CSS subiria o "De" também).
  const rotuloMes = (() => {
    const t = mes.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    return t.charAt(0).toUpperCase() + t.slice(1);
  })();
  const diasComPost = dias.filter((d) => d.getMonth() === mes.getMonth() && porDia(d).length > 0);
  const d = status.data?.destinos;
  const semContas =
    status.isFetched &&
    (d?.instagram.length ?? 0) + (d?.facebook.length ?? 0) + (d?.tiktok.length ?? 0) + (d?.youtube.length ?? 0) + (d?.linkedin.length ?? 0) === 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <CalendarDays className="h-6 w-6" /> Publicações
          </h1>
          <p className="text-sm text-muted-foreground">
            Agende posts, carrosséis, reels e stories no Instagram, Facebook, TikTok, YouTube e LinkedIn.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {pode("social:manage_accounts") && (
            <Button variant="outline" asChild>
              <Link href="/social/contas">
                <UserPlus className="mr-2 h-4 w-4" /> Contas
              </Link>
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link href="/social/captacao">
              <Link2 className="mr-2 h-4 w-4" /> Leads das redes
            </Link>
          </Button>
          <Button onClick={() => abrirNovo(null)} disabled={semContas}>
            <Plus className="mr-2 h-4 w-4" /> Nova publicação
          </Button>
        </div>
      </div>

      {semContas && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Nenhuma conta conectada. Conecte o Instagram ou uma Página do Facebook em{" "}
            <strong>Configurações → Integrações</strong>, e o TikTok, o YouTube ou o LinkedIn em{" "}
            <Link href="/social/contas" className="font-medium text-primary hover:underline">
              Redes sociais → Contas
            </Link>
            .
          </CardContent>
        </Card>
      )}

      {posts.isError && <p className="text-sm text-red-600">{toUserFriendlyError(posts.error)}</p>}

      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))} aria-label="Mês anterior">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))} aria-label="Próximo mês">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const d = new Date();
            setMes(new Date(d.getFullYear(), d.getMonth(), 1));
          }}
        >
          Hoje
        </Button>
        <h2 className="ml-2 text-lg font-medium">{rotuloMes}</h2>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
        {/* Celular: lista por dia — a grade de 7 colunas não cabe sem rolar de lado. */}
        <div className="space-y-3 md:hidden">
          {diasComPost.length === 0 && <p className="text-sm text-muted-foreground">Nada agendado neste mês.</p>}
          {diasComPost.map((d) => (
            <div key={d.toISOString()} className="space-y-1">
              <p className={`text-xs font-medium ${ehHoje(d) ? "text-primary" : "text-muted-foreground"}`}>
                {d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })}
              </p>
              {porDia(d).map((p) => (
                <ChipPost key={p.id} p={p} onClick={() => abrirPost(p)} />
              ))}
            </div>
          ))}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <div className="grid min-w-[700px] grid-cols-7 overflow-hidden rounded-lg border">
            {DIAS_CURTOS.map((d) => (
              <div key={d} className="border-b bg-muted/40 px-2 py-1 text-xs font-medium text-muted-foreground">
                {d}
              </div>
            ))}
            {dias.map((d) => {
              const doMes = d.getMonth() === mes.getMonth();
              const lista = porDia(d);
              const passado = d.getTime() + 864e5 < Date.now();
              return (
                <div
                  key={d.toISOString()}
                  role="button"
                  tabIndex={0}
                  onClick={() => !passado && !semContas && abrirNovo(d)}
                  onKeyDown={(e) => e.key === "Enter" && !passado && !semContas && abrirNovo(d)}
                  className={`min-h-[104px] space-y-1 border-b border-r p-1 ${doMes ? "" : "bg-muted/20 text-muted-foreground"} ${passado ? "cursor-default" : "cursor-pointer hover:bg-muted/30"}`}
                >
                  <div className="flex justify-end">
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${ehHoje(d) ? "bg-primary text-primary-foreground" : ""}`}
                    >
                      {d.getDate()}
                    </span>
                  </div>
                  {lista.slice(0, 4).map((p) => (
                    <ChipPost key={p.id} p={p} onClick={() => abrirPost(p)} />
                  ))}
                  {lista.length > 4 && <p className="px-1 text-[11px] text-muted-foreground">+{lista.length - 4} mais</p>}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="space-y-3">
          <Card>
            <CardContent className="space-y-2 pt-5">
              <p className="text-sm font-medium">Rascunhos sem data</p>
              {rascunhosSemData.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum. Salve um post como rascunho para guardar ideias aqui.</p>
              ) : (
                rascunhosSemData.map((p) => <ChipPost key={p.id} p={p} onClick={() => abrirPost(p)} />)
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-1.5 pt-5 text-xs">
              <p className="text-sm font-medium">Legenda</p>
              {(["scheduled", "publishing", "published", "partial", "failed", "draft"] as const).map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <span className={`h-3 w-5 rounded border ${COR_STATUS[s]}`} />
                  {POST_STATUS_LABELS[s]}
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <FlaskConical className="h-3 w-3" /> Reel de teste
              </div>
            </CardContent>
          </Card>
          {(posts.data ?? []).some((p) => p.status === "failed" || p.status === "partial") && (
            <Badge variant="destructive" className="w-full justify-center py-1">
              Há publicações com falha neste mês
            </Badge>
          )}
        </aside>
      </div>

      <PostComposer
        aberto={aberto}
        onFechar={() => setAberto(false)}
        post={selecionado}
        diaInicial={diaNovo}
        destinos={status.data?.destinos}
        armazenamentoPronto={status.data?.armazenamentoPronto ?? false}
        tiktokFotos={status.data?.capacidades.tiktokFotos ?? false}
        tamanhoDoLink={status.data?.capacidades.tamanhoDoLink}
      />
    </div>
  );
}

export default function SocialCalendar() {
  return (
    <PermissionGuard permission="social:publish">
      <DashboardLayout>
        <SocialCalendarContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
